import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<figure class=figure>
<img alt="Figure 1. Continuous Time System." src=${config.ASSETS_URL}/03/02/theory/figure-1.svg>
<figcaption class=caption>Figure 1. Continuous time system.</figcaption>
</figure>
<p>In the four upcoming lessons, basic signal processing tools are presented. The present lesson is devoted to the impulse response of continuous time systems. The vocable system should be understood in a general sense. As presented in <a href=# data-img-alt="Figure 1. Continuous Time System." data-img-src=${config.ASSETS_URL}/03/02/theory/figure-1.svg>Figure 1</a> a system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>S</mi>
   </math>
   relates an input time signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   to an output
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . It can be a physical system (the input signal is an acoustic source at a given point and the output is the pressure at another point) or a specific component of the acquisition chain. In all this course, systems are supposed to be Linear (which means that obey to the principle of superposition) and Time Invariant (the parameters of the system do not depend on time) and will be denoted by the acronym LTI.<br class=newline>
<h3 class=sectionHead>Definition</h3>
<p>
   The impulse response of a system is in a sense its dynamic signature. It corresponds to the response of the system, initially at rest, to an impulsion at time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   . For a mass-spring system, this is the displacement of the mass after a shock. The impulse response of a room can be obtained after a clap in the hand, a gunshot or the explosion of a ball. It corresponds to the echoes associated to the reflections on the walls of the room. In this lesson, we are mainly interested to its formal definition. To do so, it is first necessary to define the impulse excitation.<br class=newline>
<p>
   The rigorous mathematical definition of the impulsion is quite tricky and has been the subject of controverseries that were fixed in the middle of the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
        <mi>XX</mi>
         <mrow>
            <mi>t</mi>
            <mi>h</mi>
         </mrow>
      </msup>
   </math>
   century with the help of a huge mathematical formalism. We will then choose a naive approach by defining this function as the limit of rectangular window functions. Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   be defined as:
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-rel>↦</mo>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mi>n</mi>
                           <mo class=MathClass-bin>∕</mo>
                           <mn>2</mn>
                           <mstyle class=text>
                              <mtext class=textrm mathvariant=normal> if </mtext>
                           </mstyle>
                           <mrow>
                              <mo fence=true form=prefix>|</mo>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                              <mo fence=true form=postfix>|</mo>
                           </mrow>
                           <mo class=MathClass-rel><</mo>
                           <mn>1</mn>
                           <mo class=MathClass-bin>∕</mo>
                           <mi>n</mi>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=left>
                           <mn>0</mn>
                           <mstyle class=text>
                              <mtext class=textrm mathvariant=normal> if </mtext>
                           </mstyle>
                           <mrow>
                              <mo fence=true form=prefix>|</mo>
                              <mrow>
                                 <mi>t</mi>
                              </mrow>
                              <mo fence=true form=postfix>|</mo>
                           </mrow>
                           <mo class=MathClass-rel>≥</mo>
                           <mn>1</mn>
                           <mo class=MathClass-bin>∕</mo>
                           <mi>n</mi>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
            <mi>n</mi>
            <mo class=MathClass-rel>∈</mo>
            <mi>ℕ</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>These functions are represented in <a href=# data-img-alt="Figure 2. Sequence of window functions that lead to the Dirac <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>δ</mi></math>." data-img-src=${config.ASSETS_URL}/03/02/theory/figure-2.svg>Figure 2</a>. For increasing values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   they becomes thinner and thinner. It can be easily extrapolated that their limit is infinite in amplitude and infinitely small in duration. This limit is called Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   . It can also be said, cautiously, that the value of this function is zero everywhere except at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   for which it is infinite.
   <figure class=figure>
   <img alt="Figure 2. Sequence of window functions that lead to the Dirac <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>δ</mi></math>." src=${config.ASSETS_URL}/03/02/theory/figure-2.svg>
   <figcaption class=caption>Figure 2. Sequence of window functions that lead to the Dirac <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>δ</mi></math>.</figcaption></figure>
<p>
   The
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   functions have a common point: their integral is unity:
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mo>∫</mo>
            <mi>ℝ</mi>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <mi>t</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <msubsup>
            <mrow>
            <mo>∫</mo>
            </mrow>
            <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>∕</mo>
            <mi>n</mi>
            </mrow>
            <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>∕</mo>
            <mi>n</mi>
            </mrow>
            </msubsup>
            <mi>d</mi>
            <mi>t</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   Hence, for any signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
   </math>
   :
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mo>∫</mo>
            <mi>ℝ</mi>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <mi>t</mi>
            <mo class=MathClass-rel>=</mo>
            <mi class=qopname>lim n→+∞</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </mfrac>
            <msubsup>
            <mrow>
            <mo>∫</mo>
            </mrow>
            <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mo class=MathClass-bin>∕</mo>
            <mi>n</mi>
            </mrow>
            <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>∕</mo>
            <mi>n</mi>
            </mrow>
            </msubsup>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>d</mi>
            <mi>t</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mn>0</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   The Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   can then be seen to a tool (a distribution) that picks the value of a given signal at time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   . This function has some other interesting features. It is, for example, the derivative of the Heaviside function (which is zero for negative values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
   </math>
   and 1 for positive values thereby corresponding to a unit jump at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   ).<br class=newline>
<p>
   Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   is not only defined for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and can be shifted at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>τ</mi>
   </math>
   (it suffices to move the center of the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   functions in <a href=# data-img-alt="Figure 2: Sequence of window functions that lead to the Dirac <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>δ</mi></math>." data-img-src=${config.ASSETS_URL}/03/02/theory/figure-2.svg>Fig.2</a>). It is denoted by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>τ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and satisfy:
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
            <mo>∫</mo>
            <mi>ℝ</mi>
            </msub>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi>δ</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mstyle class=text>
               <mtext class=textrm mathvariant=normal>d</mtext>
            </mstyle>
            <mi>t</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>s</mi>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>τ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The impulse response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>h</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of system
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>S</mi>
   </math>
   is then formally defined as the response to the Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   :
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>h</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=bold-script>S</mi>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>[</mo>
               </mrow>
            </mstyle>
            <mi>δ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo fence=true form=prefix>]</mo>
               </mrow>
            </mstyle>
         </math>
      <td class=eq-no>(5)
</table>
<p>This function should be zero for negative times as it should agree to the principle of causality (the cause always precedes the effect). In addition a LTI system is stable if
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
            <mo>∫</mo>
            <mn>0</mn>
            <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi>∞</mi>
            </mrow>
            </msubsup>
            <mrow>
               <mo fence=true form=prefix>|</mo>
               <mrow>
                  <mi>h</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>|</mo>
            </mrow>
            <munder>
            <mi>t</mi>
            <mo>.</mo>
            </munder>
            <mo class=MathClass-rel><</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>∞</mi>
         </math>
      <td class=eq-no>(6)
</table>
<p>We will come back on these properties in the next lesson wich will present the method to determine the response of the system to a given excitation with the help of the impulse response. We will finish the present one by some examples of impulse responses.
<h3 class=sectionHead>Example of impulse responses</h3>
<p>
   Let
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒟</mi>
         </mrow>
         <mrow>
            <mi>Δ</mi>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   be the ideal delay system a system that reproduces exactly the input signal with a time delay
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   .
   <figure class=figure>
   <img alt="Figure 3. Delay System <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>D</mi></mrow><mrow><mi>Δ</mi><mi>t</mi></mrow></msub></math>." src=${config.ASSETS_URL}/03/02/theory/figure-3.svg>
   <figcaption class=caption>Figure 3. Delay System <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>D</mi></mrow><mrow><mi>Δ</mi><mi>t</mi></mrow></msub></math>.</figcaption></figure>
<p>
   As the system does not modify the shape of the input signal, the Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   remains and is just delayed. The impulse response of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=bold-script>𝒟</mi>
         </mrow>
         <mrow>
            <mi>Δ</mi>
            <mi>t</mi>
         </mrow>
      </msub>
   </math>
   is then
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-bin>−</mo>
      <mi>Δ</mi>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . When
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Δ</mi>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   , the system does not modify the signal and its impulse response is the Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
   .<br class=newline>
   <figure class=figure>
   <img alt="Figure 4: Simplified model of a loudspeaker." src=${config.ASSETS_URL}/03/02/theory/figure-4.svg>
   <figcaption class=caption>Figure 4: Simplified model of a loudspeaker.</figcaption></figure>
<p>A loudspeaker can be, in a rough aproach, considered as a single degree of freedom system as presented in <a href=# data-img-alt="Figure 4: Simplified model of a loudspeaker." data-img-src=${config.ASSETS_URL}/03/02/theory/figure-4.svg>Figure 4</a>. Its impulse response depends on the following physical parameters: mass of the cone
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
   </math>
   , stiffness of the spider
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   , damping ratio
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ξ</mi>
   </math>
   and delay
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>τ</mi>
   </math>
   . Concerning damping, when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ξ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   , the system has no damping and preserves energy; when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ξ</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
   </math>
   , the damping is called critical. It corresponds to the value between the underdamped regime (the system oscillates but these oscillations decreases with time) and the overdamped regime (damping is to strong and the system returns to equilibrium without any oscillations). You can modify these parameters on the applet on the right so as to visualise examples of impulse responses.<br class=newline>
<p>
   A last example corresponds to a simple problem of the reflection on a rigid wall as presented in the second applet on the right. The impulse response is not unique. Indeed it depends on the positions of the source and the receiver. It is then called source-receiver impulse response. At
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>t</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   , the impulsion leaves the source and propagates with a circular wave front. This propagation is associated to a decrease of the amplitude due to the geometric divergence. After a given time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   , the wave front reaches the receiver which records a first impulsion. The wave is then following its path and is reflected on the wall. The reflected wave reaches the receiver at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>τ</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   and the receiver is recording a second impulsion. The impulse response is then the sum of two delayed Dirac
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>δ</mi>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>h</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mi>δ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mi>δ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>τ</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
   </math>
   are the amplitudes of the the incident and reflected waves. They satisfy
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel><</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
   </math>
   due to geometric divergence. You can simulate this problem in the applet of the principle section.

<h3 class=sectionHead>Glossary</h3>
<dl>
  <dt>LTI
  <dd>Linear Time-Invariant</dd>
</dl>`;

export default theory;
